import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Shield,
  Globe,
  Server,
  Network,
  Wifi,
  Trash2,
  MoreHorizontal,
  Eye,
  Radar,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface Asset {
  id: string
  clientId: string | null
  type: string
  value: string
  parentId: string | null
  riskLevel: string
  findings: Array<Record<string, unknown>> | null
  firstSeenAt: string
  lastSeenAt: string
  lastScanAt: string | null
  isActive: boolean
  metadata: Record<string, unknown> | null
  createdAt: string
  children?: Asset[]
}

interface AssetStats {
  total: number
  active: number
  byType: Array<{ type: string; count: number }>
  byRiskLevel: Array<{ riskLevel: string; count: number }>
  recentChanges: number
}

interface ScanResult {
  domain: string
  assetsDiscovered: number
  subdomains: string[]
  openPorts: Array<{ host: string; port: number; service?: string }>
  findings: Array<{ type: string; detail: string; riskLevel: string }>
}

// ============================================
// Helpers
// ============================================

const assetTypeLabels: Record<string, string> = {
  domain: 'Domain',
  subdomain: 'Subdomain',
  ip: 'IP Address',
  port: 'Port',
  service: 'Service',
}

const assetTypeIcons: Record<string, typeof Globe> = {
  domain: Globe,
  subdomain: Network,
  ip: Server,
  port: Wifi,
  service: Shield,
}

function riskBadge(riskLevel: string) {
  switch (riskLevel) {
    case 'critical':
      return <Badge className="bg-red-600/10 text-red-600 border-red-600/20">Critical</Badge>
    case 'high':
      return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">High</Badge>
    case 'medium':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Medium</Badge>
    case 'low':
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Low</Badge>
    case 'info':
      return <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20">Info</Badge>
    default:
      return <Badge variant="secondary">{riskLevel}</Badge>
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getRiskColor(riskLevel: string): string {
  switch (riskLevel) {
    case 'critical': return 'bg-red-600'
    case 'high': return 'bg-orange-500'
    case 'medium': return 'bg-yellow-500'
    case 'low': return 'bg-blue-500'
    default: return 'bg-gray-400'
  }
}

// ============================================
// Create Asset Dialog
// ============================================

function CreateAssetDialog() {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState('domain')
  const [value, setValue] = useState('')
  const [riskLevel, setRiskLevel] = useState('info')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/attack-surface/assets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attack-surface'] })
      toast({ title: 'Asset added', description: 'New asset has been added to monitoring' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to add asset', variant: 'destructive' }),
  })

  function resetForm() {
    setType('domain')
    setValue('')
    setRiskLevel('info')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline"><Plus className="mr-2 h-4 w-4" /> Add Asset</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Asset</DialogTitle>
          <DialogDescription>Manually add an asset to your attack surface inventory.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Asset Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="domain">Domain</SelectItem>
                <SelectItem value="subdomain">Subdomain</SelectItem>
                <SelectItem value="ip">IP Address</SelectItem>
                <SelectItem value="port">Port</SelectItem>
                <SelectItem value="service">Service</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Value *</Label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === 'domain' ? 'example.com' : type === 'ip' ? '192.168.1.1' : 'Enter value'} />
          </div>
          <div className="space-y-2">
            <Label>Risk Level</Label>
            <Select value={riskLevel} onValueChange={setRiskLevel}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({ type, value, riskLevel })}
            disabled={!value || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Asset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Scan Domain Dialog
// ============================================

function ScanDomainDialog() {
  const [open, setOpen] = useState(false)
  const [domain, setDomain] = useState('')
  const [scanResult, setScanResult] = useState<ScanResult | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const scanMutation = useMutation({
    mutationFn: (data: { domain: string }) => apiClient.post<ScanResult>('/attack-surface/scan', data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['attack-surface'] })
      const data = response.data
      if (data) {
        setScanResult(data)
      }
      toast({ title: 'Scan complete', description: `Discovered ${data?.assetsDiscovered ?? 0} assets` })
    },
    onError: () => toast({ title: 'Error', description: 'Scan failed', variant: 'destructive' }),
  })

  function handleClose() {
    setOpen(false)
    setDomain('')
    setScanResult(null)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <Button><Radar className="mr-2 h-4 w-4" /> Scan Domain</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Scan Domain</DialogTitle>
          <DialogDescription>Discover subdomains, IPs, open ports, and services for a domain.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {!scanResult ? (
            <>
              <div className="space-y-2">
                <Label>Domain *</Label>
                <Input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="example.com"
                  disabled={scanMutation.isPending}
                />
              </div>
              {scanMutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Scanning domain... This may take a moment.
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <div className="text-2xl font-bold">{scanResult.assetsDiscovered}</div>
                    <p className="text-xs text-muted-foreground">Assets Found</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <div className="text-2xl font-bold">{scanResult.subdomains.length}</div>
                    <p className="text-xs text-muted-foreground">Subdomains</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 text-center">
                    <div className="text-2xl font-bold text-orange-500">{scanResult.openPorts.length}</div>
                    <p className="text-xs text-muted-foreground">Open Ports</p>
                  </CardContent>
                </Card>
              </div>

              {scanResult.subdomains.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Discovered Subdomains</p>
                  <div className="flex flex-wrap gap-1">
                    {scanResult.subdomains.map((sub) => (
                      <Badge key={sub} variant="secondary" className="text-xs">{sub}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {scanResult.openPorts.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Open Ports</p>
                  <div className="space-y-1">
                    {scanResult.openPorts.map((p) => (
                      <div key={`${p.host}:${p.port}`} className="text-xs flex items-center gap-2">
                        <Badge variant="outline">{p.port}</Badge>
                        <span className="text-muted-foreground">{p.host}</span>
                        {p.service && <span className="font-medium">{p.service}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scanResult.findings.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    Findings
                  </p>
                  <div className="space-y-2">
                    {scanResult.findings.map((f, i) => (
                      <div key={i} className="text-xs p-2 rounded border bg-muted/50">
                        <div className="flex items-center gap-2 mb-1">
                          {riskBadge(f.riskLevel)}
                          <span className="font-medium">{f.type}</span>
                        </div>
                        <p className="text-muted-foreground">{f.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          {!scanResult ? (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button
                onClick={() => scanMutation.mutate({ domain })}
                disabled={!domain || scanMutation.isPending}
              >
                {scanMutation.isPending ? 'Scanning...' : 'Start Scan'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Asset Detail Dialog
// ============================================

function AssetDetailDialog({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const { data: detailData } = useQuery({
    queryKey: ['attack-surface', 'asset', asset.id],
    queryFn: () => apiClient.get<Asset>(`/attack-surface/assets/${asset.id}`),
  })

  const detail = detailData?.data || asset
  const children = (detail as Asset & { children?: Asset[] }).children || []
  const TypeIcon = assetTypeIcons[detail.type] || Globe

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5" />
            {detail.value}
          </DialogTitle>
          <DialogDescription>{assetTypeLabels[detail.type] || detail.type}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Risk Level</p>
              <div className="mt-0.5">{riskBadge(detail.riskLevel)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant={detail.isActive ? 'default' : 'secondary'}>
                {detail.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">First Seen</p>
              <p className="text-sm font-medium">{formatDate(detail.firstSeenAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Seen</p>
              <p className="text-sm font-medium">{formatDate(detail.lastSeenAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Last Scan</p>
              <p className="text-sm font-medium">{formatDate(detail.lastScanAt)}</p>
            </div>
            {detail.metadata && (detail.metadata as Record<string, unknown>).service ? (
              <div>
                <p className="text-xs text-muted-foreground">Service</p>
                <p className="text-sm font-medium">{String((detail.metadata as Record<string, unknown>).service)}</p>
              </div>
            ) : null}
          </div>

          {/* Findings */}
          {detail.findings && Array.isArray(detail.findings) && detail.findings.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Findings</p>
              <div className="space-y-2">
                {detail.findings.map((f: Record<string, unknown>, i: number) => (
                  <div key={i} className="text-xs p-2 rounded border bg-muted/50">
                    {f.riskLevel ? riskBadge(String(f.riskLevel)) : null}
                    <p className="text-muted-foreground mt-1">{String(f.detail || f.description || JSON.stringify(f))}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Child Assets */}
          {children.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Child Assets ({children.length})</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {children.map((child: Asset) => {
                  const ChildIcon = assetTypeIcons[child.type] || Globe
                  return (
                    <div key={child.id} className="flex items-center justify-between text-xs p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <ChildIcon className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium">{child.value}</span>
                        <Badge variant="outline" className="text-[10px] h-4">{child.type}</Badge>
                      </div>
                      {riskBadge(child.riskLevel)}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Risk Heatmap
// ============================================

function RiskHeatmap({ stats }: { stats: AssetStats | null }) {
  if (!stats) return null

  const riskOrder = ['critical', 'high', 'medium', 'low', 'info']
  const riskMap = new Map(stats.byRiskLevel.map(r => [r.riskLevel, r.count]))
  const total = stats.total || 1

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Risk Heatmap</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {riskOrder.map((level) => {
            const count = riskMap.get(level) || 0
            const pct = Math.round((count / total) * 100)
            return (
              <div key={level} className="flex items-center gap-3">
                <span className="text-xs w-16 text-right capitalize">{level}</span>
                <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${getRiskColor(level)} transition-all`}
                    style={{ width: `${Math.max(pct, count > 0 ? 2 : 0)}%` }}
                  />
                </div>
                <span className="text-xs w-8 text-muted-foreground">{count}</span>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================
// Asset Tree View
// ============================================

function AssetTreeView({ assets }: { assets: Asset[] }) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()

  // Build tree: top-level = domains/assets without parents
  const topLevel = assets.filter(a => !a.parentId)

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
        // Fetch children
        queryClient.invalidateQueries({ queryKey: ['attack-surface', 'asset', id] })
      }
      return next
    })
  }

  function TreeNode({ asset, depth }: { asset: Asset; depth: number }) {
    const isExpanded = expandedIds.has(asset.id)
    const TypeIcon = assetTypeIcons[asset.type] || Globe

    const { data: detailData } = useQuery({
      queryKey: ['attack-surface', 'asset', asset.id],
      queryFn: () => apiClient.get<Asset & { children: Asset[] }>(`/attack-surface/assets/${asset.id}`),
      enabled: isExpanded,
    })

    const children = detailData?.data?.children || []

    return (
      <div>
        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50 cursor-pointer"
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
          onClick={() => toggleExpand(asset.id)}
        >
          <ChevronRight className={`h-3 w-3 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          <TypeIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium flex-1">{asset.value}</span>
          <Badge variant="outline" className="text-[10px] h-4">{asset.type}</Badge>
          {riskBadge(asset.riskLevel)}
        </div>
        {isExpanded && children.map((child: Asset) => (
          <TreeNode key={child.id} asset={child} depth={depth + 1} />
        ))}
      </div>
    )
  }

  if (topLevel.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Globe className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
          <p className="text-lg font-medium text-muted-foreground">No assets found</p>
          <p className="text-sm text-muted-foreground">Scan a domain or add assets manually</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="pt-4 pb-2">
        {topLevel.map(asset => (
          <TreeNode key={asset.id} asset={asset} depth={0} />
        ))}
      </CardContent>
    </Card>
  )
}

// ============================================
// Main Page
// ============================================

export function AttackSurfacePage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [riskFilter, setRiskFilter] = useState<string>('all')
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch assets
  const { data: assetsData, isLoading } = useQuery({
    queryKey: ['attack-surface', 'assets', typeFilter, riskFilter],
    queryFn: () => apiClient.get<{ data: Asset[]; meta: unknown }>('/attack-surface/assets', {
      ...(typeFilter !== 'all' && { type: typeFilter }),
      ...(riskFilter !== 'all' && { riskLevel: riskFilter }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['attack-surface', 'stats'],
    queryFn: () => apiClient.get<AssetStats>('/attack-surface/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/attack-surface/assets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attack-surface'] })
      toast({ title: 'Deleted', description: 'Asset has been removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete asset', variant: 'destructive' }),
  })

  const assets = assetsData?.data?.data || assetsData?.data || []
  const stats = statsData?.data || null

  const filtered = Array.isArray(assets)
    ? assets.filter((a: Asset) =>
        a.value.toLowerCase().includes(search.toLowerCase())
      )
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Attack Surface</h1>
          <p className="text-muted-foreground">Monitor and discover your external attack surface</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['attack-surface'] })}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <CreateAssetDialog />
          <ScanDomainDialog />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total Assets</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.total ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.active ?? 0} active</p>
          </CardContent>
        </Card>
        {stats?.byType?.slice(0, 3).map((t) => {
          const TypeIcon = assetTypeIcons[t.type] || Globe
          return (
            <Card key={t.type}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <TypeIcon className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground capitalize">{assetTypeLabels[t.type] || t.type}s</p>
                </div>
                <div className="text-2xl font-bold mt-1">{t.count}</div>
              </CardContent>
            </Card>
          )
        })}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <p className="text-xs text-muted-foreground">Recent Changes</p>
            </div>
            <div className="text-2xl font-bold text-orange-500 mt-1">{stats?.recentChanges ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Last 24 hours</p>
          </CardContent>
        </Card>
      </div>

      {/* Risk Heatmap + Content */}
      <div className="grid gap-6 md:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          {/* Tabs */}
          <Tabs defaultValue="table">
            <TabsList>
              <TabsTrigger value="table">Table View</TabsTrigger>
              <TabsTrigger value="tree">Tree View</TabsTrigger>
            </TabsList>

            <TabsContent value="table" className="space-y-4">
              {/* Filters */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Search assets..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="domain">Domain</SelectItem>
                    <SelectItem value="subdomain">Subdomain</SelectItem>
                    <SelectItem value="ip">IP Address</SelectItem>
                    <SelectItem value="port">Port</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Risk" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Risks</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="info">Info</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              {isLoading ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex gap-4">
                          <Skeleton className="h-5 w-48" />
                          <Skeleton className="h-5 w-20" />
                          <Skeleton className="h-5 w-20" />
                          <Skeleton className="h-5 w-32" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : filtered.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Shield className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
                    <p className="text-lg font-medium text-muted-foreground">No assets found</p>
                    <p className="text-sm text-muted-foreground">Scan a domain or add assets manually</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Risk</TableHead>
                        <TableHead>First Seen</TableHead>
                        <TableHead>Last Seen</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((asset: Asset) => {
                        const TypeIcon = assetTypeIcons[asset.type] || Globe
                        return (
                          <TableRow key={asset.id} className="cursor-pointer" onClick={() => setSelectedAsset(asset)}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <TypeIcon className="h-4 w-4 text-muted-foreground" />
                                {asset.value}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{assetTypeLabels[asset.type] || asset.type}</Badge>
                            </TableCell>
                            <TableCell>{riskBadge(asset.riskLevel)}</TableCell>
                            <TableCell>
                              <span className="text-sm">{formatDate(asset.firstSeenAt)}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{formatDate(asset.lastSeenAt)}</span>
                            </TableCell>
                            <TableCell>
                              <Badge variant={asset.isActive ? 'default' : 'secondary'} className="text-xs">
                                {asset.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedAsset(asset); }}>
                                    <Eye className="mr-2 h-4 w-4" /> View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(asset.id); }}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete Asset
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="tree">
              <AssetTreeView assets={Array.isArray(assets) ? assets : []} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <RiskHeatmap stats={stats} />

          {/* Asset Type Breakdown */}
          {stats && stats.byType.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Asset Types</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.byType.map((t) => {
                    const TypeIcon = assetTypeIcons[t.type] || Globe
                    return (
                      <div key={t.type} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <TypeIcon className="h-3 w-3 text-muted-foreground" />
                          <span className="capitalize">{assetTypeLabels[t.type] || t.type}</span>
                        </div>
                        <span className="font-mono text-muted-foreground">{t.count}</span>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Asset Detail Dialog */}
      {selectedAsset && (
        <AssetDetailDialog
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
        />
      )}
    </div>
  )
}
